import DOMPurify from 'dompurify';

/**
 * Regex pattern to match template variable placeholders like {{variableName}}
 * Captures the full placeholder including curly braces
 */
const PLACEHOLDER_PATTERN = /\{\{[^}]+\}\}/g;

/**
 * UUID-like marker used to temporarily replace placeholders during sanitization
 * Format: __PLACEHOLDER_{index}_{random}__
 */
const PLACEHOLDER_MARKER_PREFIX = '__PLACEHOLDER_';

/**
 * Restricts absolute URI schemes in href/src to http, https and mailto; relative
 * and protocol-relative URLs are still permitted. Set explicitly to mirror the
 * backend sanitize-html `allowedSchemes` and to block vectors like
 * javascript:/data: regardless of DOMPurify defaults. Exported so URL inputs
 * (e.g. the link dialog) can validate against the same rule before sanitizing.
 */
export const ALLOWED_URI_REGEXP = /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.:-]|$))/i;

/**
 * DOMPurify configuration for email templates.
 * Allows email-safe HTML elements and attributes while blocking XSS vectors.
 */
const SANITIZE_CONFIG = {
  // Allowed HTML tags for email templates.
  // Wrapper tags (html, head, meta, body) are intentionally excluded: the body
  // content is extracted before sanitizing and this function returns only the
  // sanitized body HTML (the caller re-wraps it), so any wrapper tags appearing
  // inside the content are stripped to prevent nested document structures.
  ALLOWED_TAGS: [
    'p',
    'br',
    'a',
    'b',
    'i',
    'u',
    'h1',
    'h2',
    'h3',
    'ul',
    'ol',
    'li',
    'table',
    'tr',
    'td',
    'th',
    'div',
    'span',
    'img',
    'pre',
    'blockquote',
    'hr',
    'code',
    'figure',
    'figcaption',
    'strong',
    'em',
  ],

  // Allowed attributes on elements.
  // Wrapper-only attributes (lang, http-equiv, content) are omitted since
  // wrapper tags are no longer allowed inside the content.
  ALLOWED_ATTR: [
    'href',
    'src',
    'alt',
    'title',
    'width',
    'height',
    'style',
    'colspan',
    'rowspan',
    'align',
  ],

  ALLOWED_URI_REGEXP,

  // Allow style attribute
  ALLOW_DATA_ATTR: false,

  // Keep content of removed tags (don't strip inner text)
  KEEP_CONTENT: true,

  // Don't force body tag (we handle that separately)
  FORCE_BODY: false,

  // Disable all hooks (cleaner processing)
  HOOKS: {},
};

/**
 * Whitelist of safe CSS properties allowed in inline styles.
 * These are styles known to work reliably in email clients.
 */
const SAFE_STYLE_PROPERTIES = new Set([
  'color',
  'font-size',
  'font-weight',
  'text-align',
  'background-color',
  'font-family',
  'line-height',
  'margin',
  'margin-top',
  'margin-bottom',
  'margin-left',
  'margin-right',
  'padding',
  'padding-top',
  'padding-bottom',
  'padding-left',
  'padding-right',
  'border',
  'border-top',
  'border-bottom',
  'border-left',
  'border-right',
  'display',
  'text-decoration',
  'font-style',
]);

/**
 * Filters inline styles to allow only safe email-compatible properties.
 * Removes any potentially dangerous CSS like expressions, behaviors, etc.
 *
 * @param element - DOM element to filter styles on
 */
function filterInlineStyles(element: Element): void {
  const style = element.getAttribute('style');
  if (!style) return;

  // Parse style string and filter to safe properties
  const styleDeclarations = style.split(';').filter((decl) => decl.trim());
  const safeDeclarations: string[] = [];

  styleDeclarations.forEach((declaration) => {
    const colonIndex = declaration.indexOf(':');
    if (colonIndex === -1) return;

    const property = declaration.substring(0, colonIndex).trim().toLowerCase();
    const value = declaration.substring(colonIndex + 1).trim();

    // Check if property is in whitelist
    if (SAFE_STYLE_PROPERTIES.has(property)) {
      // Additional check: ensure value doesn't contain dangerous keywords
      const lowerValue = value.toLowerCase();
      const isDangerous = lowerValue.includes('expression') || lowerValue.includes('behavior');
      if (!isDangerous) {
        safeDeclarations.push(`${property}: ${value}`);
      }
    }
  });

  if (safeDeclarations.length === 0) {
    element.removeAttribute('style');
  } else {
    element.setAttribute('style', safeDeclarations.join('; '));
  }
}

/**
 * Preserves template variable placeholders (e.g., {{applicant.name}}) by temporarily
 * replacing them with markers before sanitization, then restoring after.
 *
 * @param html - HTML string that may contain placeholders
 * @param direction - 'preserve' to replace placeholders with markers, 'restore' to reverse
 * @returns Array [modifiedHtml, placeholders] where placeholders is a Map for restoring
 */
function handlePlaceholders(
  html: string,
  direction: 'preserve' | 'restore',
  placeholders?: Map<string, string>,
): [string, Map<string, string>] {
  if (direction === 'preserve') {
    const foundPlaceholders = new Map<string, string>();
    let index = 0;

    const modified = html.replace(PLACEHOLDER_PATTERN, (match): string => {
      // Append a random suffix so a marker can never collide with literal
      // text a user typed into the editor (e.g. "__PLACEHOLDER_0__"), which
      // would otherwise be rewritten into a template variable on restore.
      const marker = `${PLACEHOLDER_MARKER_PREFIX}${index}_${Math.random().toString(36).slice(2)}__`;
      foundPlaceholders.set(marker, match);
      index++;
      return marker;
    });

    return [modified, foundPlaceholders];
  } else {
    // Restore mode
    if (!placeholders) {
      return [html, new Map<string, string>()];
    }

    let restored = html;
    placeholders.forEach((placeholder, marker) => {
      // Markers contain only `_`, digits and base-36 chars, so regex-safe.
      restored = restored.replace(new RegExp(marker, 'g'), placeholder);
    });

    return [restored, placeholders];
  }
}

/**
 * Extracts inner HTML from <body> tag in a wrapped HTML document.
 * Returns the original content if it doesn't match the expected format.
 * Supports both lowercase <body> and uppercase <BODY> tags, with or without attributes.
 *
 * @param wrappedHtml - Full HTML document with body tag
 * @returns Inner content of the body tag
 */
export function extractBodyContent(wrappedHtml: string): string {
  const bodyMatch = wrappedHtml.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  if (bodyMatch && bodyMatch[1]) {
    return bodyMatch[1];
  }
  return wrappedHtml;
}

/**
 * Checks whether HTML content is empty of any meaningful text or media.
 * Used for MAIL template body validation, where the stored value is
 * a full HTML document (e.g. `<body><p></p></body>`), so a plain
 * `body.trim()` check never sees an empty string.
 *
 * @param html - Raw HTML string (wrapped or unwrapped)
 * @returns true if the body has no visible text and no image content
 */
export function isBodyEmpty(html: string): boolean {
  if (!html || typeof html !== 'string') {
    return true;
  }

  const bodyContent = extractBodyContent(html);
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = bodyContent;

  if (tempDiv.querySelector('img')) {
    return false;
  }

  return tempDiv.textContent === null || tempDiv.textContent.trim().length === 0;
}

/**
 * Sanitizes HTML content for email templates.
 * - Removes XSS vectors (scripts, event handlers, dangerous attributes)
 * - Preserves safe HTML formatting (tables, lists, links, inline styles)
 * - Protects template variable placeholders ({{...}} syntax)
 * - Filters inline styles to email-safe properties only
 * - Sanitizes only the <body> content (wrapper tags are stripped and not re-added)
 *
 * @param html - Raw HTML string to sanitize (wrapped or unwrapped)
 * @returns Sanitized HTML string safe for email templates
 *
 * @example
 * const raw = '<!doctype html><html lang="ja"><head>...</head><body><p>Hello {{applicant.name}}</p><script>alert("xss")</script></body></html>';
 * const safe = sanitizeEmailHtml(raw);
 * // Result: Body HTML with script removed, placeholders preserved
 */
export function sanitizeEmailHtml(html: string): string {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // Step 0: Extract body content (in case full document is passed)
  // This ensures we only sanitize the content, not the wrapper structure
  const bodyContent = extractBodyContent(html);

  // Step 1: Replace placeholders with temporary markers
  const [htmlWithMarkers, placeholders] = handlePlaceholders(bodyContent, 'preserve');

  // Step 2: Sanitize with DOMPurify (only the body content, not the wrapper)
  const sanitized = DOMPurify.sanitize(htmlWithMarkers, SANITIZE_CONFIG);

  // Step 3: Parse result and filter inline styles
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = sanitized;

  // Recursively filter styles on all elements
  const filterStyles = (node: Element): void => {
    filterInlineStyles(node);
    node.querySelectorAll('*').forEach((child) => {
      filterInlineStyles(child);
    });
  };

  filterStyles(tempDiv);

  const styleFilteredHtml = tempDiv.innerHTML;

  // Step 4: Restore placeholders
  const [finalHtml] = handlePlaceholders(styleFilteredHtml, 'restore', placeholders);

  return finalHtml;
}
